import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { formatDateTime, money, paymentLabel } from '@/lib/format'
import type { Receipt } from '@yui-receipt/shared'

type ReceiptListResponse = {
  receipts: Receipt[]
  total: number
  page: number
  limit: number
}

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

export default function ReceiptListPage() {
  const [from, setFrom] = useState<string>('')
  const [to, setTo] = useState<string>('')

  const { data, isLoading, error, refetch, isFetching } = useQuery<ReceiptListResponse>({
    queryKey: ['receipts', from, to],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (from) params.set('from', `${from}T00:00:00Z`)
      if (to) params.set('to', `${to}T23:59:59Z`)
      params.set('limit', '50')
      return api<ReceiptListResponse>(`/receipts?${params.toString()}`)
    },
  })

  const receipts = data?.receipts ?? []

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      <section className="card">
        <h1 className="text-xl font-semibold mb-3">ประวัติใบเสร็จ</h1>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="label">ตั้งแต่</label>
            <input
              className="input"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              max={todayIso()}
            />
          </div>
          <div>
            <label className="label">ถึง</label>
            <input
              className="input"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              max={todayIso()}
            />
          </div>
          <button
            className="btn-secondary"
            onClick={() => {
              setFrom('')
              setTo('')
            }}
          >
            ล้างตัวกรอง
          </button>
          <button className="btn-primary" onClick={() => refetch()}>
            {isFetching ? 'กำลังโหลด…' : '🔄 รีเฟรช'}
          </button>
        </div>
      </section>

      <section className="card">
        {isLoading ? (
          <p className="text-slate-500 text-center py-8">กำลังโหลด…</p>
        ) : error ? (
          <p className="text-rose-600 text-center py-8">
            โหลดใบเสร็จไม่สำเร็จ — ลองรีเฟรช
          </p>
        ) : receipts.length === 0 ? (
          <p className="text-slate-500 text-center py-8">ไม่พบรายการ</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="py-2 px-2">เลขที่</th>
                  <th className="py-2 px-2">วันที่</th>
                  <th className="py-2 px-2">คนไข้</th>
                  <th className="py-2 px-2">ชำระ</th>
                  <th className="py-2 px-2 text-right">รวม</th>
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {receipts.map((r) => (
                  <tr
                    key={r.id}
                    className={
                      'border-t border-slate-100 ' +
                      (r.voidedAt ? 'opacity-50 line-through' : '')
                    }
                  >
                    <td className="py-2 px-2 font-mono">
                      {r.receiptNo ?? <span className="text-slate-400">#{r.id}</span>}
                    </td>
                    <td className="py-2 px-2">{formatDateTime(r.createdAt)}</td>
                    <td className="py-2 px-2">
                      {r.customer?.nameTh ?? (
                        <span className="text-slate-400">Walk-in</span>
                      )}
                    </td>
                    <td className="py-2 px-2">{paymentLabel(r.paymentMethod)}</td>
                    <td className="py-2 px-2 text-right font-medium">
                      {money(r.total)}
                    </td>
                    <td className="py-2 px-2 text-right">
                      <Link
                        to={`/receipts/${r.id}`}
                        className="text-brand-600 hover:underline"
                      >
                        ดู
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
